import { Box } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "react-query";

import AllReportsTable from "./AllReportsTable";
import style from "./erasure-reports-table.scss";
import ManageEditReportView from "./ManageEditReportView";
import ReportViewKebabMenu from "./views/ReportViewKebabMenu";
import { Licenses } from "components/icons/Licenses";
import KebabMenu from "components/kebab-menu/KebabMenu";
import ApplicationLayout from "components/layout/ApplicationLayout";
import { AUTOPILOT_RECHECK_LICENSE } from "components/licenses/bundles";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { ExportDetails } from "components/reports/AllReportsView";
import ExportReportsView from "components/reports/export/ExportReportsView";
import ImportReportsView from "components/reports/import/ImportReportsView";
import { REPORTS_ROUTE } from "components/router/Routes";
import Tooltip from "components/tooltip/Tooltip";
import { AUTH_REPORT_EXPORT_ALL, AUTH_REPORT_IMPORT } from "domain/authority";
import { CustomReportView, Path } from "domain/reports";
import { autopilotService } from "services/report/AutopilotService";
import { reportService } from "services/report/erasure/ReportService";
import { hasTenantCookie } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

export interface Props {
    viewDetails: CustomReportView;
}

const QUICK_FILTER_ENROLLED = "Enrolled";
const QUICK_FILTER_NOT_ENROLLED = "Not enrolled";
const QUICK_FILTER_OTHER = "Other";
const AUTOPILOT_OTHER_STATUS_VALUES = [QUICK_FILTER_ENROLLED, QUICK_FILTER_NOT_ENROLLED];
const AUTOPILOT_ENROLLMENT_STATUS_FIELD = "blancco_hardware_report.system.autopilot.enrollment_status";

export default function AutopilotReportsView(props?: Props): JSX.Element {
    const { t } = useTranslation();
    const theme = useTheme();
    const [refreshingCount, setRefreshingCount] = React.useState(0);
    function onRefreshing() {
        setRefreshingCount((prevState) => prevState + 1);
    }
    const [exportDetails, setExportDetails] = React.useState<ExportDetails | null>(null);
    const [exportVisibility, setExportVisibility] = React.useState(false);
    const [licenseCountVisibility, setLicenseCountVisibility] = React.useState(false);
    const [selectedQuickFilter, setSelectedFilter] = React.useState(QUICK_FILTER_ENROLLED);
    const [totalReportCount, setTotalReportCount] = React.useState<number>(0);
    const [isExportReportButtonDisabled, setIsExportReportButtonDisabled] = React.useState(false);
    const [showReportTable, setShowReportTable] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState(true);
    const [editReportUuid, setEditReportUuid] = React.useState<string>("");

    const viewTitle = props?.viewDetails?.name || t("Common.autopilotEnrollments");
    const viewDetails = React.useMemo((): CustomReportView => {
        return {
            uuid: "",
            name: "",
            columns: [
                "blancco_hardware_report.system.model",
                "blancco_hardware_report.system.manufacturer",
                "blancco_hardware_report.system.serial",
                Path.AUTOPILOT_STATE,
                Path.AUTOPILOT_ENROLLMENT_QUERY_TIMESTAMP,
                "creationDate",
                Path.AUTOPILOT_RECHECK_STATUS,
                Path.AUTOPILOT_RECHECK_STATUS_DATE,
                Path.AUTOPILOT_RECHECK_TIMESTAMP,
                Path.AUTOPILOT_RECHECK_PREVIOUS_ENROLLMENT_STATUS,
                Path.AUTOPILOT_RECHECK_ENROLLMENT_STATUS_CHANGED,
                Path.ERASURE_STATE,
                "blancco_erasure_report.erasures.erasure.erasure_standard_name",
                Path.SYSTEM_UUID,
                Path.SYSTEM_OFFLINE_DEVICE_ID,
                "uuid",
            ],
            shared: false,
            own: true,
            filters: {
                filters:
                    selectedQuickFilter === QUICK_FILTER_OTHER
                        ? [
                              {
                                  name: AUTOPILOT_ENROLLMENT_STATUS_FIELD,
                                  value: "true",
                                  operator: "EXIST" as const,
                                  pathType: "STRING",
                              },
                              ...AUTOPILOT_OTHER_STATUS_VALUES.map((status) => ({
                                  name: AUTOPILOT_ENROLLMENT_STATUS_FIELD,
                                  value: status,
                                  operator: "NOT_EQUAL" as const,
                                  pathType: "STRING",
                                  logicOperator: "AND" as const,
                              })),
                          ]
                        : [
                              {
                                  name: AUTOPILOT_ENROLLMENT_STATUS_FIELD,
                                  value: selectedQuickFilter,
                                  operator: "EQUAL" as const,
                                  pathType: "STRING",
                              },
                          ],
            },
        };
    }, [selectedQuickFilter, props?.viewDetails]);

    const kebabMenu = viewDetails && (
        <Box className={style.kebabMenuContainer}>
            <KebabMenu>
                <ReportViewKebabMenu
                    name={viewTitle}
                    viewDetails={viewDetails}
                    onViewDelete={() => {}}
                    shared={viewDetails.shared}
                    hideEdit={true}
                    hideDelete={true}
                />
            </KebabMenu>
        </Box>
    );

    const queryClient = useQueryClient();
    const { data: autoPilotLicenses } = useQuery(["availableLicenses", AUTOPILOT_RECHECK_LICENSE], () =>
        autopilotService.fetchAvailableLicenses()
    );

    const updateAvailableLicenses = React.useCallback(
        (consumedLicenses: number) => {
            queryClient.setQueryData(
                ["availableLicenses", AUTOPILOT_RECHECK_LICENSE],
                (currentAvailableLicenses: { availableLicenses: number } | undefined) => {
                    if (currentAvailableLicenses?.availableLicenses == null) {
                        return currentAvailableLicenses;
                    }
                    return {
                        availableLicenses: currentAvailableLicenses.availableLicenses - consumedLicenses,
                    };
                }
            );
        },
        [queryClient]
    );

    function updateFilter(selected: string) {
        setSelectedFilter(selected);
        onRefreshing();
    }

    const quickFilterButtons = React.useMemo(
        () => [
            {
                isSelected: selectedQuickFilter === QUICK_FILTER_ENROLLED,
                onSelected: () => updateFilter(QUICK_FILTER_ENROLLED),
                identifier: QUICK_FILTER_ENROLLED,
                testId: testIds.workArea.report.filters.autopilot.enrolledButton,
                text: t("Autopilot.status.enrolled"),
            },
            {
                isSelected: selectedQuickFilter === QUICK_FILTER_NOT_ENROLLED,
                onSelected: () => updateFilter(QUICK_FILTER_NOT_ENROLLED),
                identifier: QUICK_FILTER_NOT_ENROLLED,
                testId: testIds.workArea.report.filters.autopilot.notEnrolledButton,
                text: t("Autopilot.status.notEnrolled"),
            },
            {
                isSelected: selectedQuickFilter === QUICK_FILTER_OTHER,
                onSelected: () => updateFilter(QUICK_FILTER_OTHER),
                identifier: QUICK_FILTER_OTHER,
                testId: testIds.workArea.report.filters.autopilot.otherButton,
                text: t("Common.other"),
            },
        ],
        [selectedQuickFilter]
    );

    const exportButton = exportVisibility &&
        userSessionService.userHasAllAuthorities([AUTH_REPORT_EXPORT_ALL]) &&
        !hasTenantCookie() && (
            <ExportReportsView
                isAllReportsView={props?.viewDetails?.uuid == "undefined" || props?.viewDetails?.uuid == ""}
                columns={viewDetails.columns}
                filters={viewDetails.filters}
                startDate={exportDetails?.startDate}
                searchQuery={exportDetails ? exportDetails.searchQuery : ""}
                endDate={exportDetails?.endDate}
                exportAllButton={true}
                totalReportCount={totalReportCount}
                disableExportReportButton={isExportReportButtonDisabled}
            />
        );

    const importButton = !hasTenantCookie() && userSessionService.userHasAllAuthorities([AUTH_REPORT_IMPORT]) && (
        <ImportReportsView />
    );

    const licenseAvailability = licenseCountVisibility && autoPilotLicenses !== undefined && (
        <div className={style.availableLicensesContainer}>
            <Licenses size={20} color={theme.textColor} />
            <Tooltip content={t("Autopilot.tooltip.available_licenses")}>
                <div data-testid={testIds.common.primaryView.table.availableLicensesLabel}>
                    {autoPilotLicenses.availableLicenses}
                </div>
            </Tooltip>
        </div>
    );

    const createViewActions = () => {
        return (
            <>
                {licenseAvailability}
                {exportButton}
                {importButton}
            </>
        );
    };

    function onDetailsChanged(exportDetails: ExportDetails) {
        setExportDetails(exportDetails);
    }
    React.useEffect(() => {
        setLoading(false);
    });

    return (
        <>
            <ApplicationLayout
                viewTitle={viewTitle}
                view={
                    !showReportTable ? (
                        <AllReportsTable
                            count={refreshingCount}
                            fetchAllReportsService={reportService}
                            viewDetails={viewDetails}
                            onReportDelete={onRefreshing}
                            onDetailsChanged={onDetailsChanged}
                            refreshCount={refreshingCount}
                            setExportVisibility={setExportVisibility}
                            setTotalReportCount={setTotalReportCount}
                            setDisableExportReportButton={setIsExportReportButtonDisabled}
                            showForm={setShowReportTable}
                            editReportUuid={setEditReportUuid}
                            hideDialogView={setShowReportTable}
                            hasOnlyBmsReports={true}
                            hideEdit={true}
                            quickFilterButtons={quickFilterButtons}
                            setLicenseCountVisibility={setLicenseCountVisibility}
                            availableLicenses={autoPilotLicenses?.availableLicenses}
                            onLicenseUpdate={updateAvailableLicenses}
                        />
                    ) : loading ? (
                        <LoadingIndicator />
                    ) : (
                        <ManageEditReportView hideDialogView={setShowReportTable} reportUuid={editReportUuid} />
                    )
                }
                mainRoute={REPORTS_ROUTE}
                kebabMenu={kebabMenu}
                viewActions={createViewActions()}
                onHandleRefresh={onRefreshing}
                enableRefresh={true}
            />
        </>
    );
}
